import { SquaresFourIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";
import {
  findLedgerSection,
  ledgerGroupTitleKey,
  LEDGER_HUB_LINK,
} from "../../constants/ledger-sections";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import { useLedgerSections } from "../../hooks/useLedgerSections";
import classes from "./LedgerSectionNav.module.css";

/**
 * Breadcrumb + group tabs shown above every `/ledger/*` screen. Reads the
 * shared section list, so it never drifts from the sidebar or the hub.
 */
export function LedgerSectionNav() {
  const { t } = useTranslation("layout");
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { groups } = useLedgerSections();
  const section = findLedgerSection(pathname);
  if (!section) return null;

  // Тайлангийн бүлэгт сонгосон хугацаа табаас таб руу дамжина.
  const period = searchParams.get("period") ?? (searchParams.get("dateFrom") ?? "").slice(0, 7);
  const tabLink = (x: { link: string }) =>
    section.group === "report" && /^\d{4}-\d{2}$/.test(period) ? `${x.link}?period=${period}` : x.link;

  const tabs = groups.find((g) => g.key === section.group)?.sections ?? [];
  const crumbs = [
    { label: t("nav.ledgerHub"), to: LEDGER_HUB_LINK },
    { label: t(ledgerGroupTitleKey(section.group)) },
    { label: t(section.titleKey) },
  ];

  return (
    <nav className={classes.wrap} aria-label={t("nav.ledgerHub")}>
      <div className={classes.top}>
        <Breadcrumb data={crumbs} />
        <Link to={LEDGER_HUB_LINK} className={classes.all}>
          <SquaresFourIcon size={15} />
          {t("ledgerHub.allSections")}
        </Link>
      </div>
      <div className={classes.tabs} role="tablist">
        {tabs.map((x) => {
          const active = x.link === section.link;
          if (x.disabled)
            return (
              <span key={x.key} className={`${classes.tab} ${classes.tabDisabled}`} aria-disabled="true">
                {t(x.titleKey)}
              </span>
            );
          return (
            <Link
              key={x.key}
              to={tabLink(x)}
              role="tab"
              aria-selected={active}
              className={`${classes.tab} ${active ? classes.tabActive : ""}`}
            >
              {t(x.titleKey)}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/** Route element for `/ledger`: section nav on top, the screen below. */
export function LedgerLayout() {
  const { pathname } = useLocation();
  const isHub = pathname.replace(/\/$/, "") === LEDGER_HUB_LINK;
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {isHub ? null : <LedgerSectionNav />}
      <Outlet />
    </div>
  );
}
